/* eslint-disable arrow-body-style */
import React, { useMemo } from 'react';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import { cloneDeep } from 'lodash-es';
import CommonBubble from '@home/components/CommonBubble';
import { useIntl } from 'umi';
import classNames from 'classnames';
import './index.scss';
import { languageDefault } from './_constant';
import { IProps } from './_type';

/**
 * MetricsCascadeFilter 组件
 * 
 * 这个组件是一个级联筛选器，用于根据给定的选项列表和选中的值来筛选市场或地区。
 * 它接受一系列的属性，包括选项列表、选中的值、语言设置、最大和最小长度限制等。
 * 组件内部使用了 useMemo 和 useCallback 钩子来优化性能，避免不必要的重新渲染。
 * 
 * 属性:
 * - className: 额外的类名，用于自定义样式。
 * - value: 当前选中的值，包含市场和地区信息。
 * - language: 语言设置，默认为 languageDefault。
 * - options: 筛选选项列表。
 * - locale: 地区设置。
 * - maxSelectedNum: 最大可选数量，默认为一个非常大的数，实际上不起限制作用。
 * - minSelectedNum: 最小可选数量，默认为 0。
 * - showBubble: 是否显示气泡提示，默认为 false。
 * - regionKey: 地区信息的键名，默认为 'region',也是value中的type类型
 * - marketKey: 市场信息的键名，默认为 'market',也是value中的type类型
 * - onChange: 当选中值改变时的回调函数。
 * - onClose: 当筛选器关闭时的回调函数。
 * - radioOptions: 单选按钮选项，默认包含一个 'All' 选项。
 * 
 * 返回:
 * - 渲染一个包含级联筛选器的 div 元素。
 */
const MetricsCascadeFilter = (props: IProps) => {
  const {
    className,
    value: propsValue,
    language = languageDefault,
    options,
    locale,
    maxSelectedNum = 1000000000000000000000,
    minSelectedNum = 0,
    showBubble = false,
    regionKey = 'region',
    marketKey = 'market',
    onChange,
    onClose,
    radioOptions = [
      {
        value: '255',
        text: 'All',
        textCN: '全部',
      }
    ],
    ...resetProps
  } = props;
  const { formatMessage } = useIntl();

  // value指重新处理，处理为地区组件需要的格式
  const value = useMemo(() => ({
    markets: propsValue?.[marketKey],
    regions: propsValue?.[regionKey],
    type: propsValue?.type === regionKey ? 'region' : 'market',
  }), [propsValue]);

  // 关闭时调用
  const handleCloseRegionSelect = (value: any) => {
    onClose?.(value);
  };

  /**
 * 更新 allList 中的子级对象，不在 targetList 中的子级添加 disabled 属性
 * @param {Array} allList - 原始数据数组
 * @param {Array} targetList - 选中的子级 value 数组
 * @returns {Array} 更新后的数据数组
 */
  const formatOptionsWithDisabled = (allList: Array<any>, targetList: Array<string>,  maxLen: number): Array<any> =>{
  // 将 targetList 转换为 Set，方便快速查找
    const selectedSet = new Set(targetList);
    // 遍历 allList
    return allList.map(item => {
    // 如果有 children，遍历并更新子项
      const childrenHasSlected = item.children?.some(child => selectedSet.has(child.value));
      if (item.children && item.children.length > 0) {
        item.children = item.children.map(child => ({
          ...child,
          // 如果 child 的 value 不在 selectedSet 中，添加 disabled 属性
          ...(selectedSet.has(child.value) ? {} : { disabled: true }),
        }));
      }
      return {
        ...item,
        // 如果超过了最大选中数量且 item 的 value 不在 selectedSet 中，添加 disabled 属性
        disabled: targetList?.length >= maxLen && !childrenHasSlected,
      };
    });
  };

  // 处理 options，添加禁止选择逻辑
  const newOptions = useMemo(() => {
    // 大于最大选中数量，则添加禁止选择逻辑
    if(((value?.markets || [])?.length) >= maxSelectedNum) {
      const optionsArr = cloneDeep(options) || [];
      return formatOptionsWithDisabled(optionsArr, value?.markets || [], maxSelectedNum);
    }
    return options;
  }, [maxSelectedNum, options, value]);


  return (
    <div className='dbd-cascade-filter'>
      <CommonBubble
        className={"dbd-cascade-filter-bubble"}
        overlayClassName={'dbd-cascade-filter-bubble-overlay'}
        content={formatMessage({ id: 'benchmark_less_selected_metrics' })}
        visible={showBubble && value?.markets?.length < minSelectedNum}
        escapeWithReference
        flipped={false}
        destroyOnClose={false}
      >
        <NewRegionSelect
          language={language}
          value={value}
          tagNumber={10}
          className={classNames('dbd-cascade-filter-select', className)}
          // selectFilterType={'category'} 
          dropdownClassName='region-selector-dropdown'
          radioOptions={radioOptions}
          options={newOptions}
          onCloseReginSelect={handleCloseRegionSelect}
          onChange={(value) => {
            const regionVal = value?.regions ?? [];
            let marketVal = value?.markets ?? [];
            if(marketVal?.length >= maxSelectedNum) {
              marketVal = marketVal?.slice(0, maxSelectedNum);
            }
            const newValue = {
              [regionKey]: regionVal,
              [marketKey]: marketVal,
              type: value?.type === 'region' ? regionKey : marketKey,
            };
            onChange?.(newValue);
            handleCloseRegionSelect(newValue);
          }}
          closeOnScroll={true}
          popularList={[]}
          locale={locale}
          { ...resetProps || {}}
        />
      </CommonBubble>
    </div>
  );
};

export default MetricsCascadeFilter;
